// @ts-strict-ignore
import {inject, Injectable, signal} from "@angular/core";
import {AssessableItemGroup, Checklist, ChecklistItem, ChecklistItemType, UserRecordingChecklist} from "@models";
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {CdkDragDrop} from "@angular/cdk/drag-drop";
import {AssessableContextService, AssessableFormState, AssessableItemFormState, RemoveItemParams} from "./assessment-context.service";
import {FormArrayHelpers} from "./form-array-helpers";
import {BehaviorSubject, combineLatest, defer, Observable} from "rxjs";
import {GeneratedTypePredicates, SimChecklistService, SimMaterialDialogService, SimRecordingsService} from "@services";
import {debounceTime, filter, map, startWith, tap} from "rxjs/operators";
import {cloneDeep, merge} from "lodash-es";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";

export type ChecklistItemFormState = AssessableItemFormState & {
	selectableOptions: FormArray<FormControl<string>>;
	stars: FormControl<number>;
};

@UntilDestroy()
@Injectable()
export class ChecklistContextService extends AssessableContextService<ChecklistItem, ChecklistItemFormState> {
	// State
	private _isInitialized = false;
	private _checklist = new BehaviorSubject<Checklist>(null);

	// Injections
	private _fb = inject(FormBuilder);
	private _dialogService = inject(SimMaterialDialogService);
	private _simChecklistService = inject(SimChecklistService);
	private _simRecordingsService = inject(SimRecordingsService);

	readonly #isFormValid = signal(false);
	public $assessable: Observable<Checklist> = combineLatest([
		this._checklist.asObservable(),
		defer(() => this.form.valueChanges.pipe(startWith(this.form.value))),
	]).pipe(
		// do not use the form value from the combineLatest, as it might not be in sync with the form
		map(([checklist]) => this._getModelWithFormState(checklist, this.form.value)),
	);
	public $savingPipeline = defer(() => this.form.valueChanges).pipe(
		debounceTime(500),
		filter(() => this.form.valid),
		map((value) => this._getModelWithFormState(this._checklist.getValue(), value)),
		tap((checklist) => this.saveAssessable(checklist)),
	);
	public form: FormGroup<AssessableFormState<ChecklistItemFormState>>;
	public isFormValid = this.#isFormValid.asReadonly();

	public initialize(checklist: Checklist | UserRecordingChecklist) {
		if (this._isInitialized) {
			return;
		}

		this._checklist.next(checklist);
		this.form = this._fb.group({
			assessableGroups: this._fb.array(checklist.assessableGroups.map((group) => this._createChecklistGroupFormGroup(group))),
			ungroupedItems: this._fb.array(checklist.ungroupedItems.map((item) => this._createChecklistItemFormGroup(item))),
		});

		this.form.statusChanges.pipe(untilDestroyed(this)).subscribe((status) => this.#isFormValid.set(status === "VALID"));

		if (GeneratedTypePredicates.isUserRecordingChecklist(checklist)) {
			this._simRecordingsService.getRecordingById(checklist.scenarioRecordingId).then((recording) => {
				this._scenarioRecording = recording;
			});
		}

		this._isInitialized = true;
	}

	/**
	 * Get an immutable copy of the current assessable, merged with the form state
	 */
	public getAssessable(): Checklist {
		const checklist = this._checklist.getValue();
		return this._getModelWithFormState(checklist, this.form.value);
	}

	public async addGroup() {
		const checklist = cloneDeep(this._checklist.getValue());

		let group = await this._simChecklistService.addAssessableGroup({
			assessableId: checklist.id,
			name: "",
		});
		// 1. update the model
		checklist.assessableGroups.push(group);
		this._checklist.next(checklist);

		// 2. update the form
		this.form.controls.assessableGroups.push(this._createChecklistGroupFormGroup(group));

		return group.groupId;
	}

	public removeGroup(index: number) {
		this.form.controls.assessableGroups.removeAt(index);
	}

	public async addItemToGroup(groupIndex: number | null) {
		const checklist = this.getAssessable();
		const groupId = groupIndex === null ? null : checklist.assessableGroups[groupIndex].groupId;

		let serverResponseItem = await this._simChecklistService.addAssessableItem({
			assessableId: checklist.id,
			title: "",
			type: ChecklistItemType.ThreeStarOption,
			groupId: groupId,
			note: null,
		});
		// 1. update the model
		const item = this._getItemWithChangedType(serverResponseItem, ChecklistItemType.ThreeStarOption);
		if (groupIndex === null) {
			checklist.ungroupedItems.push(item);
		} else {
			checklist.assessableGroups[groupIndex].items.push(item);
		}
		this._checklist.next(checklist);

		// 2. update the form
		if (groupIndex === null) {
			this.form.controls.ungroupedItems.push(this._createChecklistItemFormGroup(item));
		} else {
			this.form.controls.assessableGroups.controls[groupIndex].controls.items.push(this._createChecklistItemFormGroup(item));
		}

		return {
			groupId: groupId,
			itemId: item.assessableItemId,
		};
	}

	public removeItem({itemIndex, groupIndex}: RemoveItemParams) {
		if (groupIndex === null) {
			this.form.controls.ungroupedItems.removeAt(itemIndex);
		} else {
			const groupControl = this.form.controls.assessableGroups.controls.at(groupIndex);
			groupControl.controls.items.removeAt(itemIndex);
		}
	}

	public addSelectableOption(itemControl: FormGroup<ChecklistItemFormState>) {
		itemControl.controls.selectableOptions.push(this._fb.control("", Validators.required));
	}

	public removeSelectableOption(itemControl: FormGroup<ChecklistItemFormState>, $event: number) {
		itemControl.controls.selectableOptions.removeAt($event);
	}

	public dropGroupPanel(event: CdkDragDrop<FormArray, FormArray>) {
		if (event.previousContainer === event.container) {
			FormArrayHelpers.moveItemInFormArray(event.container.data, event.previousIndex, event.currentIndex);
		}
	}

	public dropUngroupedItem(event: CdkDragDrop<FormArray, FormArray>) {
		if (event.previousContainer === event.container) {
			FormArrayHelpers.moveItemInFormArray(event.container.data, event.previousIndex, event.currentIndex);
		} else {
			FormArrayHelpers.transferFormArrayItem(
				event.previousContainer.data,
				event.container.data,
				event.previousIndex,
				event.currentIndex,
			);
		}
	}

	public dropItemInsideGroup(event: CdkDragDrop<FormArray, FormArray>) {
		if (event.previousContainer === event.container) {
			FormArrayHelpers.moveItemInFormArray(event.container.data, event.previousIndex, event.currentIndex);
		} else {
			FormArrayHelpers.transferFormArrayItem(
				event.previousContainer.data,
				event.container.data,
				event.previousIndex,
				event.currentIndex,
			);
		}
	}

	public shiftGroup(dir: "up" | "down", groupIndex: number) {
		FormArrayHelpers.moveItemInFormArray(this.form.controls.assessableGroups, groupIndex, groupIndex + this._getShiftDirection(dir));
	}

	public shiftGroupedItem(dir: "up" | "down", groupIndex: number, itemIndex: number) {
		const groupControl = this.form.controls.assessableGroups.controls.at(groupIndex);
		FormArrayHelpers.moveItemInFormArray(groupControl.controls.items, itemIndex, itemIndex + this._getShiftDirection(dir));
	}

	public shiftUngroupedItem(dir: "up" | "down", itemIndex: number) {
		FormArrayHelpers.moveItemInFormArray(this.form.controls.ungroupedItems, itemIndex, itemIndex + this._getShiftDirection(dir));
	}

	// Careful, this method should resemble the counterpart in the assessable-panel-base.component.ts
	public handleItemTypeChange(item: ChecklistItem, itemType: ChecklistItemType) {
		// get updated item model
		const updatedItem = this._getItemWithChangedType(item, itemType);

		// update model
		const checklist = this.getAssessable();
		const group = checklist.assessableGroups.find((g) => g.items.some((i) => i.assessableItemId === updatedItem.assessableItemId));
		if (group) {
			const itemIndex = group.items.findIndex((i) => i.assessableItemId === updatedItem.assessableItemId);
			group.items[itemIndex] = updatedItem;
		} else {
			const itemIndex = checklist.ungroupedItems.findIndex((i) => i.assessableItemId === updatedItem.assessableItemId);
			checklist.ungroupedItems[itemIndex] = updatedItem;
		}
		this._checklist.next(checklist);

		// handle form updates
		this._updateItemTypeInForm(updatedItem, itemType);
	}

	/**
	 * Saves the assessable to the server.
	 */
	public async saveAssessable(checklist: Checklist) {
		await this._simChecklistService.updateChecklist({
			checklistId: checklist.id,
			assessableGroups: checklist.assessableGroups,
			ungroupedItems: checklist.ungroupedItems,
		});
	}

	public moveGroupedItemToUngroupedItems(groupIndex: number, index: number) {
		const checklist = this.getAssessable();
		const group = checklist.assessableGroups[groupIndex];
		const item = cloneDeep(group.items[index]);
		this.removeItem({itemIndex: index, groupIndex});
		this.form.controls.ungroupedItems.push(this._createChecklistItemFormGroup(item));
	}

	public async changeActivationCondition(group: AssessableItemGroup<ChecklistItem>): Promise<void> {
		// Not implemented for checklists
	}

	public async moveItemToOtherGroup(itemIndex: number, groupIndex: number) {
		const checklist = this.getAssessable();
		const result = await this._dialogService.openMoveChecklistItemToGroup({
			checklistGroups: checklist.assessableGroups,
		});
		if (result.action !== "ok") {
			return;
		}
		const item = groupIndex === null ? checklist.ungroupedItems[itemIndex] : checklist.assessableGroups[groupIndex].items[itemIndex];
		const mutableItem = cloneDeep(item);
		const {selectedChecklistGroupIndex} = result;
		this.removeItem({itemIndex, groupIndex});

		this.form.controls.assessableGroups
			.at(selectedChecklistGroupIndex)
			.controls.items.push(this._createChecklistItemFormGroup(mutableItem));
	}

	/// Form State Helpers
	private _createChecklistGroupFormGroup(group: AssessableItemGroup<ChecklistItem>) {
		return this._fb.group({
			// Required to keep track of the group
			id: group.groupId,
			name: [group.name ?? "", Validators.required],
			note: group.note ?? "",
			items: this._fb.array(group.items.map((item) => this._createChecklistItemFormGroup(item))),
		});
	}

	private _createChecklistItemFormGroup(item: ChecklistItem) {
		return this._fb.group({
			id: item.assessableItemId,
			title: [item.title ?? "", Validators.required],
			note: item.note,
			selectableOptions: this._fb.array(item.selectableOptions.map((option) => this._fb.control(option, Validators.required))),
			stars: this._fb.control(item.maxStars ?? 3),
		});
	}

	private _getItemWithChangedType(item: ChecklistItem, itemType: ChecklistItemType): ChecklistItem {
		const mutableItem = cloneDeep(item);
		mutableItem.type = itemType;

		switch (mutableItem.type) {
			case ChecklistItemType.SingleChoiceOption:
			case ChecklistItemType.MultipleChoiceOption:
				break;
			case ChecklistItemType.ThreeStarOption:
				mutableItem.maxStars = 3;
				mutableItem.selectableOptions = [];
				break;
			default:
				mutableItem.selectableOptions = [];
		}

		return mutableItem;
	}

	private _getShiftDirection(dir: "up" | "down"): -1 | 1 {
		return dir === "up" ? -1 : 1;
	}

	private _getModelWithFormState(checklist: Checklist, formValue: FormGroup<AssessableFormState<ChecklistItemFormState>>["value"]) {
		const mergedAssessment = merge({}, checklist, formValue);
		mergedAssessment.assessableGroups = formValue.assessableGroups.map(mapGroup);
		mergedAssessment.ungroupedItems = formValue.ungroupedItems.map(mapItem);
		return mergedAssessment;

		// local functions
		function mapGroup(formGroup: (typeof formValue.assessableGroups)[0]): AssessableItemGroup<ChecklistItem> {
			const modelGroup = checklist.assessableGroups.find((g) => g.groupId === formGroup.id);
			if (!modelGroup) {
				throw new Error("Group not found in model");
			}
			const mergedGroup = merge({}, modelGroup, formGroup);
			mergedGroup.items = formGroup.items.map(mapItem);

			return mergedGroup;
		}

		function mapItem(formItem: (typeof formValue.ungroupedItems)[0]): ChecklistItem {
			const items = checklist.assessableGroups.flatMap((g) => g.items).concat(checklist.ungroupedItems);
			const modelItem = items.find((i) => i.assessableItemId === formItem.id);
			if (!modelItem) {
				throw new Error("Item not found in model");
			}
			const mergedItem = merge({}, modelItem, formItem);
			mergedItem.selectableOptions = formItem.selectableOptions;
			mergedItem.maxStars = formItem.stars;

			return mergedItem;
		}
	}

	private _updateItemTypeInForm(updatedItem: ChecklistItem, itemType: ChecklistItemType) {
		const itemControl = this.findItemControl(updatedItem.assessableItemId);
		if (itemType !== ChecklistItemType.SingleChoiceOption && itemType !== ChecklistItemType.MultipleChoiceOption) {
			itemControl.controls.selectableOptions.clear();
		}
	}

	addThreeStarOption(itemControl: FormGroup<ChecklistItemFormState>) {
		const stars = itemControl.controls.stars.value;
		itemControl.controls.stars.setValue(Math.min(10, stars + 1));
	}

	removeThreeStarOption(itemControl: FormGroup<ChecklistItemFormState>) {
		const stars = itemControl.controls.stars.value;
		itemControl.controls.stars.setValue(Math.max(3, stars - 1));
	}
}
